import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  AButton,
  AInput,
  AStatisticCard,
  ContainerForm,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../../components';
import { useToast } from '../../../../hooks/toast';
import { getSelectClients, getSelectProfessionals } from '../../../../http';
import api from '../../../../services/api';
import { currency } from '../../../../shared/utils/functions';
import { getInitialFinalDateMonth } from '../../../../shared/utils/format-date';
import {
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

interface IPreviewItem {
  professional_id: string;
  professional_name: string;
  commission_percent: number;
  total_treatments: number;
  total_items: number;
  base_value: number;
  calculated_commission: number;
  adjustment_value?: number;
  final_commission: number;
  supplier_client_id?: string | null;
  supplier_name?: string | null;
  financial_id?: string | null;
  notes?: string;
  already_closed: boolean;
}

interface IHistoryItem {
  id: string;
  professional_id: string;
  professional_name: string;
  period_start_date: string;
  period_end_date: string;
  commission_percent_snapshot: number;
  total_treatments: number;
  total_items: number;
  base_value: number;
  calculated_commission: number;
  adjustment_value: number;
  final_commission: number;
  notes?: string | null;
  closed_at: string;
  supplier_name?: string | null;
  financial_code?: number | null;
  financial_status?: number | null;
}

interface IProfessionalOption {
  id: string;
  name: string;
}

interface ISupplierOption {
  id: string;
  corporate_name: string;
}

const CommissionClosingPage: React.FC = () => {
  const getFinancialStatusLabel = useCallback((status?: number | null) => {
    if (status === 2) return <Tag color="green">Pago</Tag>;
    if (status === 1) return <Tag color="gold">Em aberto</Tag>;
    if (status === 5) return <Tag color="red">Cancelado</Tag>;
    return <Tag>Sem repasse</Tag>;
  }, []);

  const navigate = useNavigate();
  const { addToast } = useToast();
  const date = getInitialFinalDateMonth();

  const [dateInitial, setDateInitial] = useState<string | null>(date.dateI);
  const [dateFinal, setDateFinal] = useState<string | null>(date.dateF);
  const [professionalId, setProfessionalId] = useState<string | undefined>();
  const [financialStatusFilter, setFinancialStatusFilter] = useState<
    string | undefined
  >();
  const [professionals, setProfessionals] = useState<IProfessionalOption[]>([]);
  const [suppliers, setSuppliers] = useState<ISupplierOption[]>([]);
  const [previewItems, setPreviewItems] = useState<IPreviewItem[]>([]);
  const [historyItems, setHistoryItems] = useState<IHistoryItem[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [closingPeriod, setClosingPeriod] = useState(false);
  const [reopeningId, setReopeningId] = useState<string | null>(null);

  const loadProfessionals = useCallback(async () => {
    try {
      const [professionalsResponse, suppliersResponse] = await Promise.all([
        getSelectProfessionals(),
        getSelectClients(0),
      ]);
      setProfessionals(
        professionalsResponse.map((item) => ({ id: item.id, name: item.name })),
      );
      setSuppliers(
        suppliersResponse.map((item) => ({
          id: item.id,
          corporate_name: item.corporate_name,
        })),
      );
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao carregar profissionais e fornecedores',
      });
    }
  }, [addToast]);

  const loadHistory = useCallback(async () => {
    if (!dateInitial || !dateFinal) {
      return;
    }

    setLoadingHistory(true);
    try {
      const response = await api.get('/commission-closings', {
        params: {
          date_initial: dateInitial,
          date_final: dateFinal,
          professional_id: professionalId,
          financial_status: financialStatusFilter,
        },
      });
      setHistoryItems(response.data);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao carregar histórico de fechamentos',
      });
    } finally {
      setLoadingHistory(false);
    }
  }, [addToast, dateFinal, dateInitial, financialStatusFilter, professionalId]);

  const loadPreview = useCallback(async () => {
    if (!dateInitial || !dateFinal) {
      addToast({
        type: 'warning',
        title: 'Aviso',
        description: 'Informe o período inicial e final',
      });
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await api.post('/commission-closings/preview', {
        date_initial: dateInitial,
        date_final: dateFinal,
        professional_id: professionalId,
      });

      setPreviewItems(
        response.data.items.map((item: IPreviewItem) => ({
          ...item,
          adjustment_value: item.adjustment_value || 0,
          final_commission: item.final_commission || item.calculated_commission,
        })),
      );
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao apurar comissões do período',
      });
    } finally {
      setLoadingPreview(false);
    }
  }, [addToast, dateFinal, dateInitial, professionalId]);

  const handleAdjustmentChange = useCallback(
    (professional_id: string, adjustmentValue: number | null) => {
      setPreviewItems((current) =>
        current.map((item) => {
          if (item.professional_id !== professional_id) {
            return item;
          }

          const adjustment = Number(adjustmentValue || 0);

          return {
            ...item,
            adjustment_value: adjustment,
            final_commission: Number(
              (item.calculated_commission + adjustment).toFixed(2),
            ),
          };
        }),
      );
    },
    [],
  );

  const handleSupplierChange = useCallback(
    (professional_id: string, supplierClientId?: string) => {
      setPreviewItems((current) =>
        current.map((item) => {
          if (item.professional_id !== professional_id) {
            return item;
          }

          const supplier = suppliers.find(
            (supplierItem) => supplierItem.id === supplierClientId,
          );

          return {
            ...item,
            supplier_client_id: supplierClientId || null,
            supplier_name: supplier?.corporate_name || null,
          };
        }),
      );
    },
    [suppliers],
  );

  const handleNotesChange = useCallback(
    (professional_id: string, notes: string) => {
      setPreviewItems((current) =>
        current.map((item) => {
          if (item.professional_id !== professional_id) {
            return item;
          }

          return {
            ...item,
            notes,
          };
        }),
      );
    },
    [],
  );

  const handleClosePeriod = useCallback(async () => {
    if (!dateInitial || !dateFinal) {
      return;
    }

    if (dateInitial > dateFinal) {
      addToast({
        type: 'warning',
        title: 'Aviso',
        description: 'A data inicial não pode ser maior que a data final',
      });
      return;
    }

    const closableItems = previewItems.filter((item) => !item.already_closed);

    if (!closableItems.length) {
      addToast({
        type: 'warning',
        title: 'Aviso',
        description:
          'Não há profissionais pendentes de fechamento neste período',
      });
      return;
    }

    const invalidNegativeItem = closableItems.find(
      (item) => Number(item.final_commission || 0) < 0,
    );

    if (invalidNegativeItem) {
      addToast({
        type: 'warning',
        title: 'Aviso',
        description: `A comissão final de ${invalidNegativeItem.professional_name} não pode ficar negativa`,
      });
      return;
    }

    const itemMissingNotes = closableItems.find((item) => {
      const adjustmentValue = Number(item.adjustment_value || 0);
      return adjustmentValue !== 0 && !item.notes?.trim();
    });

    if (itemMissingNotes) {
      addToast({
        type: 'warning',
        title: 'Aviso',
        description: `Informe a justificativa do ajuste para ${itemMissingNotes.professional_name}`,
      });
      return;
    }

    setClosingPeriod(true);
    try {
      await api.post('/commission-closings/close-period', {
        date_initial: dateInitial,
        date_final: dateFinal,
        entries: closableItems.map((item) => ({
          professional_id: item.professional_id,
          supplier_client_id: item.supplier_client_id || undefined,
          adjustment_value: item.adjustment_value || 0,
          notes: item.notes || undefined,
        })),
      });

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Fechamento de comissões realizado com sucesso',
      });

      await Promise.all([loadPreview(), loadHistory()]);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao fechar comissões do período',
      });
    } finally {
      setClosingPeriod(false);
    }
  }, [
    addToast,
    dateFinal,
    dateInitial,
    loadHistory,
    loadPreview,
    previewItems,
  ]);

  const handleReopen = useCallback(
    async (id: string) => {
      setReopeningId(id);
      try {
        await api.patch('/commission-closings/reopen', { id });
        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Fechamento reaberto com sucesso',
        });
        await Promise.all([loadPreview(), loadHistory()]);
      } catch (error: any) {
        addToast({
          type: 'error',
          title: 'Erro',
          description:
            error?.response?.data?.message ||
            'Erro ao reabrir fechamento de comissões',
        });
      } finally {
        setReopeningId(null);
      }
    },
    [addToast, loadHistory, loadPreview],
  );

  const summary = useMemo(() => {
    return previewItems.reduce(
      (acc, item) => {
        acc.totalProfessionals += 1;
        acc.totalBase += Number(item.base_value || 0);
        acc.totalCalculated += Number(item.calculated_commission || 0);
        acc.totalAdjustment += Number(item.adjustment_value || 0);
        acc.totalFinal += Number(item.final_commission || 0);
        return acc;
      },
      {
        totalProfessionals: 0,
        totalBase: 0,
        totalCalculated: 0,
        totalAdjustment: 0,
        totalFinal: 0,
      },
    );
  }, [previewItems]);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  useEffect(() => {
    loadPreview();
    loadHistory();
  }, [loadHistory, loadPreview]);

  return (
    <PageLayout>
      <PageIntro
        badge="Relatórios"
        icon={<CalculatorOutlined />}
        title="Fechamento de comissões"
        description="Apure comissões por período, aplique ajustes manuais e mantenha histórico operacional de fechamento."
        actions={
          <Space>
            <AButton
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/financials')}
            >
              Voltar
            </AButton>
            <AButton
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleClosePeriod}
              loading={closingPeriod}
            >
              Fechar período
            </AButton>
          </Space>
        }
      />
      <PagePanel>
        <ContainerForm>
          <Row gutter={12} style={{ width: '100%', marginBottom: 16 }}>
            <Col xs={24} md={6}>
              <Typography.Text strong>Data inicial</Typography.Text>
              <AInput
                type="date"
                value={dateInitial || ''}
                onChange={(e) => setDateInitial(e.target.value)}
              />
            </Col>
            <Col xs={24} md={6}>
              <Typography.Text strong>Data final</Typography.Text>
              <AInput
                type="date"
                value={dateFinal || ''}
                onChange={(e) => setDateFinal(e.target.value)}
              />
            </Col>
            <Col xs={24} md={6}>
              <Typography.Text strong>Profissional</Typography.Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Todos os profissionais"
                value={professionalId}
                onChange={(value) => setProfessionalId(value)}
                options={professionals.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Col>
            <Col xs={24} md={6}>
              <Typography.Text strong>Status do repasse</Typography.Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Todos os status"
                value={financialStatusFilter}
                onChange={(value) => setFinancialStatusFilter(value)}
                options={[
                  { label: 'Sem repasse', value: 'none' },
                  { label: 'Em aberto', value: 'open' },
                  { label: 'Pago', value: 'paid' },
                  { label: 'Cancelado', value: 'cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} md={6}>
              <Typography.Text strong>&nbsp;</Typography.Text>
              <AButton
                type="primary"
                style={{ width: '100%' }}
                onClick={() => {
                  loadPreview();
                  loadHistory();
                }}
                loading={loadingPreview || loadingHistory}
              >
                Atualizar
              </AButton>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={6}>
              <AStatisticCard
                title="Profissionais"
                value={summary.totalProfessionals}
                color="#1677ff"
                icon={<CalculatorOutlined />}
              />
            </Col>
            <Col xs={24} md={6}>
              <AStatisticCard
                title="Base apurada"
                value={currency(summary.totalBase)}
                color="#13c2c2"
                icon={<CalculatorOutlined />}
              />
            </Col>
            <Col xs={24} md={6}>
              <AStatisticCard
                title="Comissão calculada"
                value={currency(summary.totalCalculated)}
                color="#52c41a"
                icon={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={24} md={6}>
              <AStatisticCard
                title="Comissão final"
                value={currency(summary.totalFinal)}
                color="#722ed1"
                icon={<SaveOutlined />}
              />
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Prévia de apuração
          </Typography.Title>
          <Table
            rowKey="professional_id"
            pagination={false}
            loading={loadingPreview}
            dataSource={previewItems}
            scroll={{ x: 1100 }}
            columns={[
              {
                title: 'Profissional',
                dataIndex: 'professional_name',
                fixed: 'left',
                width: 220,
              },
              {
                title: 'Status',
                dataIndex: 'already_closed',
                width: 130,
                render: (value: boolean) =>
                  value ? (
                    <Tag color="blue">Já fechado</Tag>
                  ) : (
                    <Tag color="green">Pendente</Tag>
                  ),
              },
              {
                title: 'Fornecedor',
                dataIndex: 'supplier_name',
                width: 240,
                render: (
                  _: string | null | undefined,
                  record: IPreviewItem,
                ) => (
                  <Select
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    disabled={record.already_closed}
                    placeholder="Selecionar fornecedor"
                    value={record.supplier_client_id || undefined}
                    onChange={(value) =>
                      handleSupplierChange(record.professional_id, value)
                    }
                    optionFilterProp="label"
                    options={suppliers.map((item) => ({
                      value: item.id,
                      label: item.corporate_name,
                    }))}
                  />
                ),
              },
              {
                title: 'Comissão (%)',
                dataIndex: 'commission_percent',
                width: 130,
                render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
              },
              {
                title: 'Tratamentos',
                dataIndex: 'total_treatments',
                width: 120,
              },
              {
                title: 'Itens',
                dataIndex: 'total_items',
                width: 100,
              },
              {
                title: 'Base',
                dataIndex: 'base_value',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Calculada',
                dataIndex: 'calculated_commission',
                width: 150,
                render: (value: number) => currency(value),
              },
              {
                title: 'Ajuste manual',
                dataIndex: 'adjustment_value',
                width: 160,
                render: (value: number, record: IPreviewItem) => (
                  <InputNumber
                    style={{ width: '100%' }}
                    value={value || 0}
                    disabled={record.already_closed}
                    onChange={(adjustment) =>
                      handleAdjustmentChange(record.professional_id, adjustment)
                    }
                  />
                ),
              },
              {
                title: 'Justificativa',
                dataIndex: 'notes',
                width: 260,
                render: (value: string | undefined, record: IPreviewItem) => (
                  <Input.TextArea
                    value={value}
                    disabled={record.already_closed}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    placeholder="Motivo do ajuste ou observação do fechamento"
                    onChange={(event) =>
                      handleNotesChange(
                        record.professional_id,
                        event.target.value,
                      )
                    }
                  />
                ),
              },
              {
                title: 'Final',
                dataIndex: 'final_commission',
                width: 150,
                render: (value: number) => currency(value),
              },
            ]}
          />

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            Histórico de fechamentos
          </Typography.Title>
          <Table
            rowKey="id"
            pagination={{ pageSize: 8 }}
            loading={loadingHistory}
            dataSource={historyItems}
            scroll={{ x: 1100 }}
            columns={[
              {
                title: 'Profissional',
                dataIndex: 'professional_name',
                width: 220,
              },
              {
                title: 'Fornecedor',
                dataIndex: 'supplier_name',
                width: 180,
                render: (value?: string | null) => value || 'Não vinculado',
              },
              {
                title: 'Período',
                width: 220,
                render: (_, record: IHistoryItem) =>
                  `${record.period_start_date} até ${record.period_end_date}`,
              },
              {
                title: 'Base',
                dataIndex: 'base_value',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Calculada',
                dataIndex: 'calculated_commission',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Ajuste',
                dataIndex: 'adjustment_value',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Final',
                dataIndex: 'final_commission',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Observação',
                dataIndex: 'notes',
                width: 220,
                render: (value?: string | null) => value || '-',
              },
              {
                title: 'Fechado em',
                dataIndex: 'closed_at',
                width: 180,
              },
              {
                title: 'Repasse',
                width: 160,
                render: (_, record: IHistoryItem) =>
                  record.financial_code ? (
                    <Space direction="vertical" size={2}>
                      <Typography.Text>{`Conta #${record.financial_code}`}</Typography.Text>
                      {getFinancialStatusLabel(record.financial_status)}
                    </Space>
                  ) : (
                    <Tag color="warning">Não gerado</Tag>
                  ),
              },
              {
                title: 'Ações',
                width: 130,
                render: (_, record: IHistoryItem) => (
                  <AButton
                    danger
                    icon={<ReloadOutlined />}
                    loading={reopeningId === record.id}
                    disabled={record.financial_status === 2}
                    onClick={() => handleReopen(record.id)}
                  >
                    Reabrir
                  </AButton>
                ),
              },
            ]}
          />

          <Typography.Text type="secondary" style={{ marginTop: 12 }}>
            O fechamento agora gera automaticamente conta a pagar no financeiro
            quando o profissional possui fornecedor cadastrado com o mesmo CPF.
            Quando esse vínculo não existir, você pode selecionar o fornecedor
            manualmente antes do fechamento.
          </Typography.Text>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default CommissionClosingPage;
